import { Link } from "react-router-dom";
import PropTypes from "prop-types";
import { ChevronRight } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { withoutBidiControls as plain } from "../snippets/bidi";
import MoneyMoved from "./MoneyMoved";
import TargetBreaches from "./TargetBreaches";
import TargetHoldingsList from "./TargetHoldingsList";
import UntrustedMarkdown from "./UntrustedMarkdown";
import { companyPath, formatDay, taskBoardPath, whoName } from "./lib/format";
import { formatCents, formatPct, moneyMoved } from "./lib/money";
import { CHANGE_LABELS, kindLabel, staleDraft } from "./lib/target";

const MAIN = "h-11 md:h-9";

/** "≈ +$600.00" — a buy positive, a sale with a real minus. */
function signedCents(cents) {
  return cents > 0n ? `≈ +${formatCents(cents)}` : `≈ ${formatCents(cents)}`;
}

/** Why a stale draft can't be submitted (D11, PR 4 review). */
function staleText({ copied, current }) {
  const from =
    copied === null ? "it was drafted before any target was approved" : `it was copied from v${copied}`;
  const now = current === null ? "no target is approved now" : `the current target is v${current} now`;
  const next = current === null ? "a new draft" : `a new draft from v${current}`;
  return `Stale: ${from}, and ${now}. It can't be submitted — its author starts ${next}.`;
}

/**
 * The change open against the current target (spec §5.4, walkthroughs 7–8):
 * a DIFF — each holding added, removed (to 0%) or resized, with its weights
 * before and after and its reason, and cash — roughly how much money it
 * moves at the latest snapshot, its cap breaches with their overrides, what
 * it was copied from (a stale draft can't be submitted, and says so), the
 * board task it answers and its notes (untrusted markdown, D8).
 *
 * Approve and Decline are offered only when `canDecide` (a person, the page
 * decides) on a PROPOSED change; a draft is its author's until submitted.
 */
function TargetChangePanel({ version, current, openTotal, latest, canDecide, busy, isAgent, onDecide }) {
  const proposed = version.status === "proposed";
  const diff = version.diff || { base: null, holdings: [], cash_before: null, cash_after: version.cash_weight };
  const changes = Array.isArray(diff.holdings) ? diff.holdings : [];
  const moved = latest.snapshot ? moneyMoved(changes, latest.snapshot.total_value) : null;
  const stale = staleDraft(version, current);
  const breaches = Array.isArray(version.cap_breaches) ? version.cap_breaches : [];
  const drafted = formatDay(version.created_at);
  const submitted = formatDay(version.submitted_at);

  return (
    <section aria-label="Open change" className="rounded-lg border bg-card p-4">
      <h2 className="mt-0 mb-1 text-lg font-semibold">
        Open change — v{version.number}
      </h2>
      <p className="mt-0 mb-2">
        <Badge variant={proposed ? "warning" : "outline"}>
          {proposed ? "Proposed — awaiting your decision" : "Draft — not submitted yet"}
        </Badge>
      </p>
      <p className="m-0 font-display text-xl font-semibold wrap-anywhere">{plain(version.title)}</p>
      <p className="m-0 text-sm text-muted-foreground wrap-anywhere">
        {kindLabel(version.kind)} · drafted by {whoName(version.created_by)}
        {drafted ? ` on ${drafted}` : ""}
        {proposed && version.submitted_at
          ? ` · submitted by ${whoName(version.submitted_by)}${submitted ? ` on ${submitted}` : ""}`
          : ""}
      </p>
      <p className="m-0 text-sm text-muted-foreground">
        {version.copied_from === null || version.copied_from === undefined
          ? "Copied when no target was approved"
          : `Copied from v${version.copied_from}`}
      </p>
      {version.task && (
        <p className="m-0 text-sm wrap-anywhere">
          Answers the board task{" "}
          <Link
            to={taskBoardPath(version.task.id)}
            className="inline-flex min-h-11 items-center underline underline-offset-2 md:min-h-0"
          >
            {plain(version.task.title)} (task #{version.task.id})
          </Link>
        </p>
      )}
      {openTotal > 1 && (
        <p className="mt-2 mb-0 text-sm">
          {openTotal} changes are open: this is the {proposed ? "proposed one" : "newest draft"}. Submitting another
          supersedes the rest.
        </p>
      )}
      {stale && (
        <p role="note" className="mt-2 mb-0 rounded-md border border-destructive/60 bg-destructive/5 p-3 text-sm">
          {staleText(stale)}
        </p>
      )}
      {!proposed && !stale && (
        <p className="mt-2 mb-0 text-sm">
          Its author is still writing it. You can decide it once it&apos;s submitted; cap breaches are judged then.
        </p>
      )}

      {version.notes && (
        <div className="mt-3">
          <h3 className="m-0 text-base font-semibold">The case for it</h3>
          <div className="text-sm">
            <UntrustedMarkdown body={version.notes} />
          </div>
        </div>
      )}

      <h3 className="mt-4 mb-1 text-base font-semibold">
        {diff.base === null ? "What it sets (no target before it)" : `What changes against v${diff.base}`}
      </h3>
      <ul
        aria-label={diff.base === null ? "What it sets" : `What changes against v${diff.base}`}
        className="m-0 flex list-none flex-col p-0"
      >
        {changes.map((change) => (
          <li key={change.symbol} data-symbol={change.symbol} className="border-b border-border py-2">
            <div className="flex flex-wrap items-baseline gap-x-2">
              <Link
                to={companyPath(change.symbol)}
                className="inline-flex min-h-11 min-w-11 items-center font-semibold underline-offset-2 hover:underline md:min-h-0 md:min-w-0"
              >
                {change.symbol}
              </Link>{" "}
              <Badge variant={change.change === "removed" ? "secondary" : "outline"}>
                {CHANGE_LABELS[change.change] ?? change.change}
              </Badge>{" "}
              <span className="tabular-nums">
                {formatPct(change.weight_before)} → {formatPct(change.weight_after)}
              </span>
              {moved && moved.bySymbol[change.symbol] !== undefined && (
                <>
                  {" "}
                  <span className="ms-auto text-sm tabular-nums text-muted-foreground">
                    {signedCents(moved.bySymbol[change.symbol])}
                  </span>
                </>
              )}
            </div>
            {change.reason ? (
              <p className="m-0 text-sm wrap-anywhere">{plain(change.reason)}</p>
            ) : (
              <p className="m-0 text-sm text-muted-foreground">No reason given.</p>
            )}
          </li>
        ))}
        <li data-symbol="cash" className="flex flex-wrap items-baseline gap-x-2 py-2">
          <span className="font-semibold">Cash</span>{" "}
          <span className="tabular-nums">
            {diff.cash_before === null || diff.cash_before === undefined
              ? formatPct(diff.cash_after)
              : diff.cash_before === diff.cash_after
                ? `stays ${formatPct(diff.cash_after)}`
                : `${formatPct(diff.cash_before)} → ${formatPct(diff.cash_after)}`}
          </span>
        </li>
      </ul>
      <div className="mt-2">
        <MoneyMoved changes={changes} latest={latest} />
      </div>

      <h3 className="mt-4 mb-1 text-base font-semibold">Cap breaches</h3>
      {!proposed ? (
        <p className="m-0 text-sm text-muted-foreground">Judged when it&apos;s submitted.</p>
      ) : breaches.length > 0 ? (
        <>
          <p className="mt-0 mb-2 text-sm text-muted-foreground">
            The caps are settings, not rules: each breach carries the reason it&apos;s meant.
          </p>
          <TargetBreaches breaches={breaches} version={version} />
        </>
      ) : (
        <p className="m-0 text-sm">None — every cap holds.</p>
      )}

      <details className="group mt-4">
        <summary className="flex min-h-11 cursor-pointer items-center gap-1 text-sm font-medium md:min-h-8">
          <ChevronRight
            aria-hidden="true"
            className="size-4 shrink-0 text-muted-foreground transition-transform group-open:rotate-90"
          />
          The whole target it proposes
        </summary>
        <TargetHoldingsList version={version} label={`Every holding in v${version.number}`} />
      </details>

      {proposed && canDecide && (
        <div className="mt-4 flex flex-wrap gap-2">
          <Button type="button" className={MAIN} disabled={busy} onClick={() => onDecide("approved")}>
            Approve v{version.number}…
          </Button>{" "}
          <Button type="button" variant="outline" className={MAIN} disabled={busy} onClick={() => onDecide("declined")}>
            Decline v{version.number}…
          </Button>
        </div>
      )}
      {proposed && isAgent && (
        <p className="mt-4 mb-0 text-sm text-muted-foreground">
          A person approves or declines a target change, in their own browser.
        </p>
      )}
    </section>
  );
}

TargetChangePanel.propTypes = {
  version: PropTypes.shape({
    number: PropTypes.number.isRequired,
    status: PropTypes.string.isRequired,
    kind: PropTypes.string,
    title: PropTypes.string,
    notes: PropTypes.string,
    cash_weight: PropTypes.string.isRequired,
    copied_from: PropTypes.number,
    created_by: PropTypes.object,
    created_at: PropTypes.string,
    submitted_by: PropTypes.object,
    submitted_at: PropTypes.string,
    task: PropTypes.shape({ id: PropTypes.number.isRequired, title: PropTypes.string }),
    cap_breaches: PropTypes.arrayOf(PropTypes.object),
    holdings: PropTypes.arrayOf(PropTypes.object),
    diff: PropTypes.shape({
      base: PropTypes.number,
      holdings: PropTypes.arrayOf(PropTypes.object),
      cash_before: PropTypes.string,
      cash_after: PropTypes.string,
    }),
  }).isRequired,
  /** The current (approved) target, for a draft's staleness; null before the first. */
  current: PropTypes.shape({ number: PropTypes.number.isRequired }),
  openTotal: PropTypes.number.isRequired,
  /** useLatestSnapshot's state: the money it moves. */
  latest: PropTypes.object.isRequired,
  /** A person may decide here (the page fails closed while `me` is unknown). */
  canDecide: PropTypes.bool.isRequired,
  /** The target is reloading: deciding waits for what the page will show. */
  busy: PropTypes.bool,
  isAgent: PropTypes.bool,
  /** `(decision)`: "approved" or "declined" — open the confirm. */
  onDecide: PropTypes.func.isRequired,
};

export default TargetChangePanel;
